<template>  <div class="dropdown dropdown-bottom w-full">
    <div class="relative w-full" @click="openDropdown">
      <input
        ref="inputRef"
        type="text"
        :placeholder="placeholder"
        class="input input-bordered w-full pr-10"
        :class="[inputSize, { 'input-error': error }]"
        :disabled="disabled"
        :readonly="readonly"
        v-model="inputValue"
        @input="onInput"
        @focus="onFocus"
        @blur="onBlur"
        @keydown.up.prevent="navigateList('up')"
        @keydown.down.prevent="navigateList('down')"
        @keydown.enter.prevent="selectHighlighted"
        @keydown.esc.prevent="closeDropdown"
        @click="openDropdown"
        aria-haspopup="listbox"
        :aria-expanded="isOpen"
      />
      <div class="absolute inset-y-0 right-0 pr-3 flex items-center">
        <span v-if="loading" class="loading loading-spinner loading-sm"></span>
        <button 
          v-else-if="clearable && inputValue" 
          type="button" 
          class="focus:outline-none cursor-pointer" 
          @click.stop="clearInput"
        >
          <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-base-content/50" viewBox="0 0 20 20" fill="currentColor">
            <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd" />
          </svg>
        </button>
      </div>
    </div>    
    
    <div
      v-show="isOpen"
      ref="dropdownRef"
      class="dropdown-content z-10 menu bg-base-100 rounded-box shadow-lg w-full max-h-60 overflow-auto mt-1 border border-base-300"
      @mousedown.prevent
    >
      <div v-if="loading" class="p-4 flex justify-center">
        <slot name="loading">
          <span class="loading loading-spinner"></span>
        </slot>
      </div>
      <template v-else>
        <div v-if="$slots.header" class="border-b border-base-300">
          <slot name="header"></slot>
        </div>

        <ul v-if="filteredItems.length > 0" role="listbox">
          <li 
            v-for="(item, index) in filteredItems" 
            :key="getItemKey(item, index)"
            role="option"
            :aria-selected="highlightedIndex === index"
            class="w-full"
          >
            <div
              @mousedown.prevent
              @click="selectItem(item)"
              @mouseover="highlightedIndex = index"
              class="w-full text-left cursor-pointer"
              :class="{ 'bg-primary/10': highlightedIndex === index }"
            >
              <slot name="item" :item="item" :index="index" :highlighted="highlightedIndex === index">
                {{ getItemText(item) }}
              </slot>
            </div>
          </li>
        </ul>
        
        <div v-else class="p-4 text-center text-base-content/70">
          <slot name="empty">
            No results found
          </slot>
        </div>

        <div v-if="$slots.footer" class="border-t border-base-300">
          <slot name="footer"></slot>
        </div>
      </template>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, nextTick, onMounted, onUnmounted } from 'vue'
import { useEventListener, onClickOutside } from '@vueuse/core'

// Props
const props = defineProps({
  modelValue: {
    type: String,
    default: ''
  },
  items: {
    type: Array,
    default: () => []
  },
  keySelector: {
    type: [String, Function],
    default: 'id'
  },
  textField: {
    type: [String, Function],
    default: 'text'
  },
  filter: {
    type: [Function, undefined],
  },
  placeholder: {
    type: String,
    default: 'Search...'
  },
  disabled: {
    type: Boolean,
    default: false
  },
  readonly: {
    type: Boolean,
    default: false
  },
  clearable: {
    type: Boolean,
    default: true
  },
  clearAfterSelection: {
    type: Boolean,
    default: false
  },
  loading: {
    type: Boolean,
    default: false
  },
  size: {
    type: String,
    default: 'md',
    validator: (value) => ['xs', 'sm', 'md', 'lg', 'xl'].includes(value)
  },
  error: {
    type: Boolean,
    default: false
  },
  openOnFocus: {
    type: Boolean,
    default: true
  },
  closeOnSelect: {
    type: Boolean,
    default: true
  }
})

// Emits
const emit = defineEmits([
  'update:modelValue', 
  'change', 
  'focus', 
  'blur', 
  'itemSelected', 
  'open', 
  'close',
  'input',
  'cleared'
])

// Refs
const inputRef = ref(null)
const dropdownRef = ref(null)
const inputValue = ref(props.modelValue)
const isOpen = ref(false)
const highlightedIndex = ref(-1)

// Computed
const inputSize = computed(() => {
  return props.size ? `input-${props.size}` : ''
})

const filteredItems = computed(() => {
    if (typeof props.filter === 'function' && Array.isArray(props.items)) {
        return props.items.filter(props.filter);
    }

    return props.items;
})

// Watch for external changes to modelValue
watch(() => props.modelValue, (newValue) => {
  inputValue.value = newValue
})

// Watch for internal changes to inputValue
watch(inputValue, (newValue) => {
  emit('update:modelValue', newValue)
  emit('input', newValue)
})

// Watch for open status changes
watch(isOpen, (newValue) => {
  if (newValue) {
    emit('open')
    nextTick(() => {
      if (filteredItems.value.length > 0) {
        highlightedIndex.value = 0
      }
    })
  } else {
    emit('close')
    highlightedIndex.value = -1
  }
})

// Watch filtered items to reset highlight when the list changes
watch(filteredItems, () => {
  if (isOpen.value && filteredItems.value.length > 0) {
    highlightedIndex.value = 0
  } else {
    highlightedIndex.value = -1
  }
})

// Methods
function getItemKey(item, index) {
  if (typeof props.keySelector === 'function') {
    return props.keySelector(item)
  }
  return item[props.keySelector] || index
}

function getItemText(item) {
  if (typeof props.textField === 'function') {
    return props.textField(item)
  }
  return item[props.textField] || item.toString()
}

function onInput() {
  isOpen.value = true
}

function onFocus(event) {
  emit('focus', event)
  if (props.openOnFocus) {
    isOpen.value = true
  }
}

function onBlur(event) {
  // Don't close the dropdown immediately to allow for clicks within the dropdown
  setTimeout(() => {
    // Check if the related target is within our component
    const relatedTarget = event.relatedTarget;
    if (!relatedTarget || 
        (!dropdownRef.value?.contains(relatedTarget) && 
         !inputRef.value?.contains(relatedTarget))) {
      emit('blur', event)
    }
  }, 100)
}

function clearInput() {
  inputValue.value = '';
  emit('change', '');
  emit('cleared');
  inputRef.value?.focus();
}

function openDropdown() {
  if (!props.disabled && !isOpen.value) {
    isOpen.value = true
  }
}

function closeDropdown() {
  isOpen.value = false
}

function selectItem(item) {
  const text = getItemText(item)
  inputValue.value = text
  emit('change', text)
  emit('itemSelected', item)
  
  if (props.closeOnSelect) {
    closeDropdown()
  }
  
  if (props.clearAfterSelection) {
      inputValue.value = ''
  }

  nextTick(() => {
    inputRef.value?.focus()
  })
}

function selectHighlighted() {
  if (highlightedIndex.value >= 0 && highlightedIndex.value < filteredItems.value.length) {
    selectItem(filteredItems.value[highlightedIndex.value])
  }
}

function navigateList(direction) {
  if (!isOpen.value) {
    openDropdown()
    return
  }
  
  if (filteredItems.value.length === 0) return
  
  const increment = direction === 'down' ? 1 : -1
  
  let newIndex = highlightedIndex.value + increment
  
  // Loop around if we're at the edges
  if (newIndex < 0) {
    newIndex = filteredItems.value.length - 1
  } else if (newIndex >= filteredItems.value.length) {
    newIndex = 0
  }
  
  highlightedIndex.value = newIndex
  
  nextTick(() => {
    // Scroll the highlighted item into view
    const listItems = dropdownRef.value?.querySelectorAll('li')
    if (listItems && listItems[newIndex]) {
      const item = listItems[newIndex]
      const container = dropdownRef.value
      
      const itemTop = item.offsetTop
      const itemBottom = itemTop + item.offsetHeight
      const containerTop = container.scrollTop
      const containerBottom = containerTop + container.offsetHeight
      
      if (itemTop < containerTop) {
        container.scrollTop = itemTop
      } else if (itemBottom > containerBottom) {
        container.scrollTop = itemBottom - container.offsetHeight
      }
    }
  })
}

// Close dropdown when clicking outside both the input and dropdown
onClickOutside(dropdownRef, (event) => {
  // Only close if the click is outside both the dropdown and the input
  if (isOpen.value && !inputRef.value?.contains(event.target)) {
    closeDropdown()
  }
}, { ignore: [inputRef] })

// Handle keyboard events globally
function handleKeyDown(event) {
  if (!isOpen.value) return
  
  if (event.key === 'Escape') {
    closeDropdown()
    event.preventDefault()
  }
}

onMounted(() => {
  useEventListener(document, 'keydown', handleKeyDown)
  
  // Add event listener for clicks on the document
  const handleDocumentClick = (event) => {
    // Don't close if clicking on the input or dropdown
    if (inputRef.value?.contains(event.target) || dropdownRef.value?.contains(event.target)) {
      return
    }
    
    // Close dropdown if open and clicking outside
    if (isOpen.value) {
      closeDropdown()
    }
  }
  
  document.addEventListener('click', handleDocumentClick)
  
  // Clean up event listener on component unmount
  onUnmounted(() => {
    document.removeEventListener('click', handleDocumentClick)
  })
})
</script>